/**
 * Custom Site Specific Javascript
 *
 * This file is to add custom features to the website without having to modify
 * core/common assets. Define a custom class, and build new or overwrite
 * existing methods to create custom functionality.
 */

import { ImportantDates } from "commonAssets/components/ImportantDates";
import { createElement } from "commonAssets/lib/createElement.js";
import { i18n } from "commonAssets/js/constants";
import { capturePreservedClaimSections } from "./preserveClaimSections.js";
import { validateField } from "commonAssets/lib/validateField";
import IMask from "imask";

export class customImportantDates extends ImportantDates {
    datesList() {
        const baseHtml = super.datesList();
        const container = document.createElement("div");
        container.innerHTML = baseHtml;

        const list = container.querySelector("ul");
        if (!list) return baseHtml;

        const inStoreLi = createElement("li", { class: "list-group-item important-date", id: "in-store-benefit-dates" }, [
            createElement("time", { class: "d-block fw-bold" }, [], "June 1, 2026 – June 2, 2026"),
            createElement("span", {}, [], i18n.getTranslation("in_store_benefit_dates")),
        ]);

        list.appendChild(inStoreLi);
        return container.innerHTML;
    }

    card(
        classSelector = "card bg-light text-dark border rounded p-3 impt-dates",
        idSelector = "importantDatesCard"
    ) {
        const datesCard = createElement("div", { class: classSelector, id: idSelector }, [
            createElement("h3", { class: "text-danger text-center mb-4 card-title", id: "impt-dates-title" }, [], i18n.getTranslation("important_dates")),
            createElement("div", { class: "card-body p-0", id: "impt-dates-body" }, [], this.datesList()),
            createElement("p", { class: "mt-3 px-2 text-muted fst-italic small", id: "impt-dates-passed-note" }, [], i18n.getTranslation("deadlines_passed_note")),
        ]);
        return datesCard.outerHTML;
    }
}

/**
 * Mask Price Paid / Shelf Price inputs as non-negative, 2-decimal USD.
 *
 * Binds a single delegated focusin listener on the transactions container so
 * the mask also applies to rows added later via "Item Details". Each input is
 * masked once, on first focus.
 */
function setupPriceMasks() {
    const container = document.getElementById("transactions");
    if (!container || container.dataset.priceMasksBound) return;
    container.dataset.priceMasksBound = "true";

    container.addEventListener("focusin", (event) => {
        const input = event.target;
        if (
            !input.matches?.('[id^="price_paid"], [id^="shelf_price"]') ||
            input.dataset.masked
        ) {
            return;
        }

        input.dataset.masked = "true";
        input.inputMode = "decimal";
        IMask(input, {
            mask: Number,
            scale: 2,
            min: 0,
            thousandsSeparator: "",
            radix: ".",
        });
    });
}

/**
 * Confine Date of Purchase inputs to the configured class period on every row.
 *
 * Copies the field's data-date-min/data-date-max onto native min/max so the
 * browser date picker blocks out-of-range selection, and re-validates on change
 * so typed out-of-range dates are caught. A single delegated listener pair on
 * the transactions container covers rows added later via "Item Details".
 */
function setupDateConstraints() {
    const container = document.getElementById("transactions");
    if (!container || container.dataset.dateConstraintsBound) return;
    container.dataset.dateConstraintsBound = "true";

    container.addEventListener("focusin", (event) => {
        const input = event.target;
        if (
            !input.matches?.('[id^="date_of_purchase"]') ||
            input.dataset.dateConstrained
        ) {
            return;
        }

        input.dataset.dateConstrained = "true";
        const min = input.getAttribute("data-date-min");
        const max = input.getAttribute("data-date-max");
        if (min) input.min = min;
        if (max) input.max = max;
    });

    container.addEventListener("change", (event) => {
        if (event.target.matches?.('[id^="date_of_purchase"]')) {
            validateField(event.target);
        }
    });
}

/**
 * Initializes a claim object.
 *
 * @param {Object} claim - The claim object to be initialized.
 * @returns {Object} The initialized claim object.
 */
export function customInitClaim(claim) {
    console.log("[Initiate claim]...");
    capturePreservedClaimSections(claim);

    const initFieldLock = (selector) => {
        const element = document.querySelector(selector);
        if (!element) return;
        if (element.value) element.disabled = true;
    };

    initFieldLock("#first_name");
    initFieldLock("#last_name");

    // Deficiency portal: drop the stock claim-form deadline heading.
    document.getElementById("claimFormDeadline")?.remove();

    document.querySelector("#claimFieldsTitle")?.remove();
    document.querySelector("#claimFieldsBody")?.remove();
    document.querySelector("#transactions > div.formTitle")?.remove();
    document.querySelector("#transactions > div.mb-3.formBody")?.remove();

    setupPriceMasks();
    setupDateConstraints();

    return claim;
}

/**
 * Custom function to load a member.
 *
 * @param {Object} claim - The member object to be loaded.
 * @returns {Object} - The loaded member object.
 */
export function customLoadMember(claim) {
    console.log("[Load class member]...");
    // custom code goes here
    return claim;
}

/**
 * Processes a claim for a custom member.
 *
 * @param {Object} claim - The claim to be processed.
 * @returns {Object} The processed claim.
 */
export function customProcessClaim(claim) {
    console.log("[Process claim]...");
    // custom code goes here
    return claim;
}
